import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type {
    AmountBreakdown,
    PaymentRequestDeliveryEventView,
    PaymentRequestDetailsView,
    PaymentRequestDocumentDescriptor,
} from '@anchorbase/pillar-sdk';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatusBadge } from '@/components/ui/status-badge';
import { BackButton } from '@/components/ui/back-button';
import { formatPaymentRequestSourceDocumentReference } from './source-document-reference';

const usdCurrencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

function formatAmount(amount: number | undefined, currencyCode?: string): string {
    if (amount === undefined) {
        return '—';
    }
    if (currencyCode === 'USD') {
        return usdCurrencyFormatter.format(amount);
    }
    if (currencyCode === undefined) {
        return amount.toFixed(2);
    }
    return `${amount.toFixed(2)} ${currencyCode}`;
}

function formatDateTime(value: string | undefined): string {
    if (value === undefined) {
        return '—';
    }
    return new Date(value).toLocaleString();
}

function formatBytes(value: number): string {
    if (value < 1024) {
        return `${value} B`;
    }
    if (value < 1024 * 1024) {
        return `${(value / 1024).toFixed(1)} KB`;
    }
    return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

function renderBreakdownRows(
    amountBreakdown: AmountBreakdown | undefined,
    currencyCode: string
): ReadonlyArray<{
    label: string;
    value: string;
}> {
    if (amountBreakdown === undefined) {
        return [];
    }

    return [
        { label: 'Base amount', value: formatAmount(amountBreakdown.base_amount, currencyCode) },
        { label: 'Tax', value: formatAmount(amountBreakdown.tax, currencyCode) },
        { label: 'Surcharge', value: formatAmount(amountBreakdown.surcharge, currencyCode) },
        {
            label: 'Convenience fee',
            value: formatAmount(amountBreakdown.convenience_fee, currencyCode),
        },
        { label: 'Donation', value: formatAmount(amountBreakdown.donation, currencyCode) },
        { label: 'Total', value: formatAmount(amountBreakdown.total_amount, currencyCode) },
    ].filter((row) => row.value !== '—');
}

function DescriptionListRow(props: {
    label: string;
    value: string;
}) {
    return (
        <div className="grid grid-cols-[180px_1fr] gap-3 border-b py-2 last:border-b-0">
            <dt className="text-sm font-medium text-muted-foreground">{props.label}</dt>
            <dd className="text-sm">{props.value}</dd>
        </div>
    );
}

function DocumentRow(props: {
    document: PaymentRequestDocumentDescriptor;
}) {
    const { document } = props;
    return (
        <div className="rounded-lg border p-4">
            <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                    <p className="font-medium">{document.original_filename ?? 'Generated document'}</p>
                    <p className="text-sm text-muted-foreground">
                        {document.source_kind} · {document.media_type} ·{' '}
                        {formatBytes(document.content_length_bytes)}
                    </p>
                </div>
                <div className="text-right text-xs text-muted-foreground">
                    SHA-256: {document.content_sha256.slice(0, 12)}...
                </div>
            </div>
        </div>
    );
}

function DeliveryEventRow(props: {
    deliveryEvent: PaymentRequestDeliveryEventView;
}) {
    const { deliveryEvent } = props;
    return (
        <div className="rounded-lg border p-4">
            <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                    <p className="font-medium">{deliveryEvent.provider_event_type}</p>
                    <p className="text-sm text-muted-foreground">
                        {deliveryEvent.delivery_channel} via {deliveryEvent.delivery_provider}
                    </p>
                    <p className="text-sm text-muted-foreground">
                        Message ID: {deliveryEvent.provider_message_id ?? '—'}
                    </p>
                </div>
                <div className="text-right text-sm text-muted-foreground">
                    <p>Received: {formatDateTime(deliveryEvent.received_ts)}</p>
                    <p>Provider: {formatDateTime(deliveryEvent.provider_event_ts)}</p>
                </div>
            </div>
        </div>
    );
}

function PaymentTransactionRow(props: {
    paymentTransaction: PaymentRequestDetailsView['payment_transactions'][number];
}) {
    const { paymentTransaction } = props;
    const title =
        paymentTransaction.merchant_transaction_id ?? paymentTransaction.payment_transaction_uuid;

    return (
        <div className="rounded-lg border p-4">
            <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                    {paymentTransaction.can_display ? (
                        <Link
                            className="font-medium text-primary hover:underline"
                            to={`/payment-transactions/${paymentTransaction.payment_transaction_uuid}`}
                        >
                            {title}
                        </Link>
                    ) : (
                        <p className="font-medium">{title}</p>
                    )}
                    <p className="text-sm text-muted-foreground">
                        {formatAmount(
                            paymentTransaction.original_amount,
                            paymentTransaction.currency_code
                        )}{' '}
                        · {paymentTransaction.status}
                    </p>
                </div>
                <div className="text-right text-sm text-muted-foreground">
                    <p>Created: {formatDateTime(paymentTransaction.created_ts)}</p>
                </div>
            </div>
        </div>
    );
}

export function PaymentRequestDetailView(props: {
    paymentRequest: PaymentRequestDetailsView;
    pageDescription: string;
    backHref: string;
    backTooltip: string;
    actions?: ReactNode;
    showDeliveryEvents?: boolean;
    showSupportDetails?: boolean;
    showPaymentTransactions?: boolean;
}) {
    const paymentRequest = props.paymentRequest;
    const showDeliveryEvents: boolean = props.showDeliveryEvents ?? true;
    const showPaymentTransactions: boolean = props.showPaymentTransactions ?? false;
    const formattedSourceDocumentReference = formatPaymentRequestSourceDocumentReference({
        sourceDocumentReference: paymentRequest.source_document_reference,
        sourceDocumentKey: paymentRequest.source_document_key,
    });
    const breakdownRows = renderBreakdownRows(
        paymentRequest.amount_breakdown,
        paymentRequest.currency_code
    );

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div className="space-y-1">
                    <h1 className="text-3xl font-bold">
                        {paymentRequest.title ?? formattedSourceDocumentReference ?? 'Payment Request'}
                    </h1>
                    <p className="text-sm text-muted-foreground">{props.pageDescription}</p>
                </div>
                <div className="flex items-center gap-3">
                    {props.actions}
                    <BackButton href={props.backHref} tooltip={props.backTooltip} />
                </div>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-3">
                        Summary
                        <StatusBadge status={paymentRequest.status} />
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <dl>
                        <DescriptionListRow
                            label="Reference"
                            value={formattedSourceDocumentReference ?? '—'}
                        />
                        <DescriptionListRow
                            label="Customer name"
                            value={paymentRequest.payment_request_details?.customerName ?? '—'}
                        />
                        <DescriptionListRow
                            label="Original amount"
                            value={formatAmount(
                                paymentRequest.original_amount,
                                paymentRequest.currency_code
                            )}
                        />
                        <DescriptionListRow label="Currency" value={paymentRequest.currency_code} />
                        <DescriptionListRow
                            label="Recipient email"
                            value={paymentRequest.recipient_email ?? '—'}
                        />
                        <DescriptionListRow
                            label="Recipient phone"
                            value={paymentRequest.recipient_phone ?? '—'}
                        />
                        <DescriptionListRow
                            label="Created"
                            value={formatDateTime(paymentRequest.created_ts)}
                        />
                        <DescriptionListRow
                            label="Expires"
                            value={formatDateTime(paymentRequest.expires_ts)}
                        />
                        <DescriptionListRow label="Message" value={paymentRequest.message ?? '—'} />
                    </dl>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>Amount Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                    {breakdownRows.length === 0 ? (
                        <p className="text-sm text-muted-foreground">
                            No amount breakdown is available for this payment request.
                        </p>
                    ) : (
                        <dl>
                            {breakdownRows.map((row) => (
                                <DescriptionListRow
                                    key={row.label}
                                    label={row.label}
                                    value={row.value}
                                />
                            ))}
                        </dl>
                    )}
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>Documents</CardTitle>
                </CardHeader>
                <CardContent>
                    {paymentRequest.documents.length === 0 ? (
                        <p className="text-sm text-muted-foreground">
                            No documents are attached to this payment request.
                        </p>
                    ) : (
                        <div className="space-y-3">
                            {paymentRequest.documents.map((document) => (
                                <DocumentRow
                                    key={document.payment_request_document_uuid}
                                    document={document}
                                />
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {showDeliveryEvents && (
                <Card>
                    <CardHeader>
                        <CardTitle>Delivery Events</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {paymentRequest.delivery_events.length === 0 ? (
                            <p className="text-sm text-muted-foreground">
                                No delivery events have been recorded yet.
                            </p>
                        ) : (
                            <div className="space-y-3">
                                {paymentRequest.delivery_events.map((deliveryEvent) => (
                                    <DeliveryEventRow
                                        key={deliveryEvent.payment_request_delivery_event_uuid}
                                        deliveryEvent={deliveryEvent}
                                    />
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}

            {showPaymentTransactions && (
                <Card>
                    <CardHeader>
                        <CardTitle>Payment Transactions</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {paymentRequest.payment_transactions.length === 0 ? (
                            <p className="text-sm text-muted-foreground">
                                No payment transactions are visible for this payment request.
                            </p>
                        ) : (
                            <div className="space-y-3">
                                {paymentRequest.payment_transactions.map((paymentTransaction) => (
                                    <PaymentTransactionRow
                                        key={paymentTransaction.payment_transaction_uuid}
                                        paymentTransaction={paymentTransaction}
                                    />
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}

            {props.showSupportDetails === true && (
                <Card>
                    <CardHeader>
                        <CardTitle>Support Details</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <dl>
                            <DescriptionListRow
                                label="Reconciliation"
                                value={paymentRequest.reconciled ?? '—'}
                            />
                        </dl>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
